import { ReactNode, useMemo, useState } from "react";
import { DatasetListResponse } from "datasets/types/interfaces/datasetListItem";
import { Plus, Search } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { MultiSelect } from "~/components/ui/multi-select";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDebounce } from "~/hooks/useDebounce";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { nextgenAdminEvalsPath } from "~/pages/manifold-nextgen/paths";
import { catalogDatasetsListUrl } from "~/services/catalog";
import { evalCasePath, type PartialEvalCase } from "~/services/evals";
import { CaseDetailSheet } from "./CaseDetailSheet";
import { CaseFormDialog } from "./CaseFormDialog";
import { CaseHealthMatrix } from "./CaseHealthMatrix";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { TableSkeleton } from "./EvalSkeletons";
import { useTableSort } from "./TableSort";
import { CASES_PAGE_RUNS_PER_CASE } from "./constants/page";
import { CASE_SORT_DIRECTIONS, CaseSortKey } from "./constants/sorting";
import type { DatasetRefs } from "./primitives/chips";
import { ShownCount, TruncationNotice } from "./primitives/notices";
import {
  type CaseHealthCell,
  type CaseHealthRow,
  buildCaseHistory,
  sortCaseRows,
} from "./selectors/cases";
import { collectTags } from "./selectors/facets";
import { useEvalCaseHistory, useEvalCases } from "./useEvalApi";
import { useEvalFilters } from "./useEvalFilters";

const EVAL_CASES_PAGE_TITLE = "Cases";
const SEARCH_DEBOUNCE_MS = 300;

/** Roughly the table's columns, so the placeholder stands where the table will. */
const CASE_COLUMN_WIDTHS = ["w-48", "w-28", "w-28", "w-16", "w-20", "w-56", "w-8"];

/**
 * Every case in the suite on a page of its own, reached from the evals dashboard.
 * Only the facets that act on cases are offered here — tags and datasets — since
 * the run facets of the dashboard's filter row have no place on a list of cases
 * alone.
 */
const EvalCasesPage = () => {
  useDocumentTitle("Eval cases");

  const filters = useEvalFilters();
  const [localQuery, setLocalQuery] = useState(filters.query);
  const debouncedQuery = useDebounce(localQuery, SEARCH_DEBOUNCE_MS);
  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  // Set when a cell is clicked rather than a row, so the drawer opens on that run.
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PartialEvalCase | null>(null);

  // In the app header beside the platform agent, as the other nextgen admin pages
  // do. Memoised because the header compares its actions between renders, and a
  // fresh element every render would never compare equal.
  const headerActions = useMemo(
    () =>
      [
        <Button key="new-case" size="sm" className="text-xs" onClick={() => setNewCaseOpen(true)}>
          <Plus className="size-4" aria-hidden />
          New Case
        </Button>,
      ] as [ReactNode],
    []
  );
  useNextGenHeader({ actions: headerActions });
  // Lowest pass rate first, so the cases a reviewer came to look at are on top.
  const sort = useTableSort<CaseSortKey>(CaseSortKey.PASS_RATE, "asc", CASE_SORT_DIRECTIONS);

  const {
    data: casesPage,
    isLoading: casesLoading,
    mutate: refetchCases,
  } = useEvalCases(filters.tags);
  const { data: history, isLoading: historyLoading } = useEvalCaseHistory({
    tags: filters.tags,
    agentIds: [],
    runsPerCase: CASES_PAGE_RUNS_PER_CASE,
  });
  const { data: catalogDatasetList } = useFetcher<DatasetListResponse>(catalogDatasetsListUrl);
  const catalogDatasets = catalogDatasetList?.items;

  const allCases = casesPage?.items ?? [];
  const datasets: DatasetRefs = Object.fromEntries(
    (catalogDatasets ?? []).map((dataset) => [
      dataset.id,
      { title: dataset.title, accent_color: dataset.accent_color },
    ])
  );

  // The dataset facet is applied here because the case list route filters only
  // by tag.
  const scopedCases =
    filters.datasets.length === 0
      ? allCases
      : allCases.filter((evalCase) =>
          evalCase.dataset_ids.some((id) => filters.datasets.includes(id))
        );
  const search = debouncedQuery.trim().toLowerCase();
  const rows = buildCaseHistory(scopedCases, history?.results ?? [], history?.runs ?? []).filter(
    (row) =>
      !search ||
      row.evalCase.name.toLowerCase().includes(search) ||
      (row.evalCase.description ?? "").toLowerCase().includes(search)
  );
  const sortedRows = sortCaseRows(rows, sort.sortKey, sort.direction);

  const availableTags = collectTags(allCases, []);
  const datasetOptions = collectDatasetOptions(allCases, datasets);
  const isLoading = casesLoading || historyLoading;

  const handleQueryChange = (value: string) => {
    setLocalQuery(value);
    filters.setQuery(value);
  };

  const handleSelectCase = (caseId: string) => {
    setSelectedCaseId(caseId);
    setSelectedRunId(null);
  };

  const handleSelectCell = (row: CaseHealthRow, cell: CaseHealthCell) => {
    setSelectedCaseId(row.evalCase.id);
    setSelectedRunId(cell.runId);
  };

  const handleCloseCase = () => {
    setSelectedCaseId(null);
    setSelectedRunId(null);
  };

  return (
    <>
      <AdminPage
        title={EVAL_CASES_PAGE_TITLE}
        description="Every case in the suite and how it has fared in its own recent runs."
        back={{ href: nextgenAdminEvalsPath(), label: "Back to Evals", variant: "text" }}
        width="wide"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-[320px]">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="search"
                value={localQuery}
                onChange={(event) => handleQueryChange(event.target.value)}
                placeholder="Search cases"
                className="pl-9"
                aria-label="Search cases"
              />
            </div>
            <div className="flex items-center gap-3">
              <MultiSelect
                options={availableTags.map((tag) => ({
                  value: tag,
                  label: tag,
                }))}
                value={filters.tags}
                onValueChange={filters.setTags}
                placeholder="All tags"
                className="w-[170px]"
              />
              <MultiSelect
                options={datasetOptions}
                value={filters.datasets}
                onValueChange={filters.setDatasets}
                placeholder="All datasets"
                className="w-[170px]"
              />
            </div>

            <ShownCount shown={rows.length} total={allCases.length} noun="case" />
          </div>

          <TruncationNotice
            truncations={
              casesPage?.truncated
                ? [
                    {
                      noun: "cases",
                      shown: allCases.length,
                      total: casesPage.totalCount,
                    },
                  ]
                : []
            }
          />

          {isLoading && sortedRows.length === 0 ? (
            <div className="rounded-lg border p-4">
              <TableSkeleton columns={CASE_COLUMN_WIDTHS} />
            </div>
          ) : sortedRows.length === 0 ? (
            <p className="rounded-lg border py-8 text-center text-sm text-muted-foreground">
              No cases match these filters.
            </p>
          ) : (
            <CaseHealthMatrix
              rows={sortedRows}
              datasets={datasets}
              sort={sort}
              hiddenRunCount={history?.hidden_run_count ?? 0}
              variant="admin"
              onSelectCase={handleSelectCase}
              onSelectCell={handleSelectCell}
              onDeleteCase={setPendingDelete}
            />
          )}
        </div>
      </AdminPage>

      <CaseFormDialog
        open={newCaseOpen}
        onOpenChange={setNewCaseOpen}
        datasets={(catalogDatasets ?? []).map((dataset) => ({
          id: dataset.id,
          title: dataset.title,
          accent_color: dataset.accent_color,
        }))}
        availableTags={availableTags}
        onCreated={() => void refetchCases()}
      />

      <CaseDetailSheet
        open={selectedCaseId != null}
        onOpenChange={(open) => !open && handleCloseCase()}
        caseId={selectedCaseId}
        history={rows.find((row) => row.evalCase.id === selectedCaseId) ?? null}
        initialRunId={selectedRunId}
        datasets={datasets}
        availableTags={availableTags}
        onSaved={() => void refetchCases()}
      />

      <ConfirmDeleteDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.name ?? "this case"}?`}
        description={
          <>
            The case and its definition go for good. Results already recorded in past runs stay, but
            lose their definition.
          </>
        }
        confirmLabel="Delete case"
        path={pendingDelete ? evalCasePath(pendingDelete.id) : null}
        errorMessage="Could not delete the case."
        onDeleted={() => void refetchCases()}
      />
    </>
  );
};

/**
 * Dataset facet options, drawn from the cases rather than the whole catalog. A
 * dataset with no catalog entry keeps a truncated id as its label — a case can
 * outlive its dataset, and dropping it would hide those cases.
 */
const collectDatasetOptions = (
  cases: { dataset_ids: string[] }[],
  datasets: DatasetRefs
): { value: string; label: string }[] => {
  const ids = [...new Set(cases.flatMap((evalCase) => evalCase.dataset_ids))];
  return ids
    .map((id) => ({
      value: id,
      label: datasets[id]?.title ?? `${id.slice(0, 8)}…`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
};

export default EvalCasesPage;
